<template>
  <div class="cohorts">
    <app-cells>
      <app-form-group label="Lead creation period" class="form-group--no-indent cohorts__period">
        <app-date-picker v-model="period" type="month" format="MMM YYYY" range :clearable="false" @input="loadCohorts" />
      </app-form-group>
      <app-form-group label="Granularity" class="form-group--no-indent">
        <app-filters v-model="granularity" :filters="granularityOptions" name="cohort-granularity" @change="loadCohorts" />
      </app-form-group>
      <app-form-group label="Quick payment" class="form-group--no-indent cohorts__payment">
        <app-select v-model="quickPayment" :options="paymentOptions" :reduce="item => item.value" label="label" :clearable="false" @input="loadCohorts" />
      </app-form-group>
      <app-button size="small" theme="dark" @click.native="showFilters = !showFilters" :aria-expanded="String(showFilters)">
        {{ showFilters ? 'Hide filters' : 'Audience filters' }} ({{ activeFilterCount }})
      </app-button>
    </app-cells>
    <div v-show="showFilters" class="cohorts__filters">
      <app-form-group v-for="field in filterFields" :key="field.key" :label="field.label" class="form-group--no-indent">
        <app-select
          v-model="filters[field.key]" :options="field.options" :reduce="item => item.value"
          label="label" :placeholder="field.label" multiple :closeOnSelect="false" @input="loadCohorts"
        />
      </app-form-group>
    </div>
    <p v-if="optionsError" role="alert" class="cohorts__error">{{ optionsError }} <button type="button" @click="loadOptions">Retry filter options</button></p>
    <div v-if="loading" class="cohorts__loading" aria-live="polite"><app-spinner /><span>Loading cohorts…</span></div>
    <div v-else-if="error" role="alert" class="cohorts__error">
      {{ error }} <app-button size="small" @click.native="loadCohorts">Retry</app-button>
    </div>
    <template v-else-if="result">
      <p v-if="!totalLeads" class="cohorts__help">No leads match the selected period and filters.</p>
      <div v-for="metric in metrics" :key="metric.key" class="cohorts__results">
        <section class="cohorts__column" :aria-label="`${metric.title} table`">
          <div class="cohorts__section-heading cohorts__section-heading--info">
            <h2 class="title">{{ metric.title }}</h2>
            <button
              class="cohorts__info-btn"
              type="button"
              :aria-label="`About ${metric.title}`"
              aria-haspopup="dialog"
              @click="$modal.show('cohort-analytics-info')"
            >
              i
            </button>
          </div>
          <div class="cohorts__table">
            <v-client-table
              :data="result.cohorts"
              :columns="tableColumns(metric)"
              :options="tableOptions(metric)"
              class="table-default"
            >
              <template #cohort="{ row }">
                <div class="table-default__nowrap" :title="`${row.date_from} – ${row.date_to} (UTC)`">
                  <app-checkbox :value="selected.includes(row.cohort)" :label="cohortLabel(row.cohort)" @change="toggleCohort(row.cohort, $event)" />
                  <span v-if="row.is_partial" class="cohorts__partial">Partial {{ result.granularity }}</span>
                </div>
              </template>
              <template #lead_count="{ row }">{{ row.lead_count.toLocaleString('en-US') }}</template>
              <template #marketing_expenses="{ row }">{{ formatValue(row.marketing_expenses, 'net_revenue') }}</template>
              <template v-for="(days, index) in result.checkpoint_days" v-slot:[`checkpoint_${days}`]="{ row }">
                <span :key="days" :title="checkpointInfo(row, row.checkpoints[index], metric.key)">
                  {{ formatValue(row.checkpoints[index][metric.key], metric.key) }}
                </span>
              </template>
            </v-client-table>
          </div>
          <p class="cohorts__help">Select rows to compare cohorts on the chart.</p>
        </section>
        <section class="cohorts__column" :aria-label="`${metric.chartTitle} chart`">
          <div class="cohorts__section-heading">
            <h2 class="title">{{ metric.chartTitle }}</h2>
            <app-button size="small" theme="dark" :aria-expanded="String(showChart[metric.key])" @click.native="showChart[metric.key] = !showChart[metric.key]">{{ showChart[metric.key] ? 'Hide chart' : 'Show chart' }}</app-button>
          </div>
          <template v-if="showChart[metric.key]">
            <highcharts v-if="hasChartPoints(metric.key)" :options="chartOptions(metric)" />
            <p v-else class="cohorts__help">{{ chartEmptyMessage(metric.key) }}</p>
          </template>
        </section>
      </div>
    </template>
    <modal name="cohort-analytics-info" :adaptive="true" height="auto" :scrollable="true" :width="640" class="modal-not-overflow">
      <div class="popup" role="dialog" aria-modal="true" aria-labelledby="cohort-info-title">
        <button class="popup__close" type="button" aria-label="Close explanation" @click="$modal.hide('cohort-analytics-info')"></button>
        <div id="cohort-info-title" class="popup__title">How to read cohort analytics</div>
        <div class="popup__body cohorts__explanation">
          <h3>What is a cohort?</h3>
          <p>A cohort is a group of leads created in the same month or week. Use Month / Week to change the grouping. Weeks run from Monday to Sunday; dates use UTC.</p>
          <h3>What does %CR mean?</h3>
          <p>%CR 7d shows the percentage of leads that first became Customer within 7 days of their own creation. For example, if 10 of 100 leads converted within 7 days, %CR 7d is 10%.</p>
          <p>The 14d, 30d, 60d and 90d columns work the same way. They are cumulative: a lead that converts within 7 days also counts in every longer window. Each day means a full 24 hours from lead creation.</p>
          <h3>What does Net Revenue mean?</h3>
          <p>Net Revenue 7d is the total current itinerary commission in USD for leads that first became Customer within 7 days of creation. Longer windows include those same leads plus later conversions. These are current commission amounts grouped by conversion time, not historical payment balances.</p>
          <h3>What does ROAS mean?</h3>
          <p>ROAS (Return on Ad Spend) = Net Revenue / Marketing Expenses. For example, $2,000 Net Revenue / $1,000 expenses = 2.00×. Each observation window uses cumulative Net Revenue and the same expenses for the cohort's selected creation dates.</p>
          <p>Expenses sum the available USD amounts, following Marketing Dashboard. Rows without a USD conversion are excluded from that sum and do not block the other expenses. The Mark. expenses column shows the denominator.</p>
          <p>Expense filters follow Marketing Dashboard: country, country group, ad platform and advertising source. Other audience filters, including Quick payment, change leads and revenue; expenses stay at the selected advertising scope because they cannot be assigned to individual leads.</p>
          <p>ROAS shows <strong>—</strong> if marketing expenses total zero, since division by zero is undefined, or the observation window is not complete. Zero expenses may also mean no spend data has been imported.</p>
          <h3>What counts as Customer?</h3>
          <p>The first recorded transition to Deposit paid, Full paid, Completed, Cancelled or Postponed, following the same definition as Marketing Dashboard. Later status changes do not erase that conversion.</p>
          <h3>Why do some cells show a dash?</h3>
          <p><strong>—</strong> means the observation window has not finished for the whole cohort, or the cohort has no leads. <strong>0%</strong> means the window is complete and none of its leads converted within that time.</p>
          <p><strong>Partial week / month</strong> means the selected period includes only part of that cohort. Only leads created within the selected dates are counted.</p>
          <h3>How do filters work?</h3>
          <p>Filters apply to both the total number of leads and the conversions. Double requests and lead magnets are excluded by default; you can change this under Audience filters. Data updates with the Marketing Dashboard analytics refresh.</p>
          <h3>How do I compare cohorts?</h3>
          <p>Select rows in the table to add them to the chart. Each line represents a cohort: the horizontal axis shows days since lead creation, and the vertical axis shows the section’s metric. Net Revenue and ROAS use the same cumulative conversion windows. Incomplete windows are left blank.</p>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import moment from 'moment'
import { Chart } from 'highcharts-vue'
import {
  getLeadsAnalyticsCohorts, getLeadsAnalyticsDashboardBuilderOptions,
  getCountries, getUsers, getLeadsStatuses, getLeadsAdvSources, getClimbingRoutes, getSafariNationalPark
} from '@/http'

const choices = entries => entries.map(([value, label]) => ({ value, label }))
const fields = () => [
  { key: 'country_ids', label: 'Country', options: [] },
  { key: 'country_group_codes', label: 'Country groups', options: choices([['tier_1', 'Main countries'], ['tier_2', 'Secondary countries'], ['other', 'Other'], ['unknown', 'Unknown']]) },
  { key: 'product_codes', label: 'Product', options: choices([['climb', 'Climb'], ['safari', 'Safari'], ['safari_climb', 'Safari + Climb'], ['other', 'Other']]) },
  { key: 'manager_ids', label: 'Manager', options: [] },
  { key: 'domain_ids', label: 'Domain', options: [] },
  { key: 'ad_platform_codes', label: 'Ad platform', options: choices([['google', 'Google Ads'], ['bing', 'Bing Ads'], ['yandex', 'Yandex Direct'], ['meta', 'Meta Ads'], ['manual', 'Manual'], ['other', 'Other'], ['unknown', 'Unknown']]) },
  { key: 'traffic_types', label: 'Traffic type', options: choices([['paid_ads', 'Paid ads'], ['organic', 'Organic'], ['referral', 'Referral'], ['direct', 'Direct'], ['unknown', 'Unknown']]) },
  { key: 'adv_source_ids', label: 'Advertising source', options: [] },
  { key: 'status_groups', label: 'Status group', options: choices([['other', 'Other'], ['negotiation', 'Negotiation'], ['customer', 'Customers'], ['lost', 'Lost'], ['potential_lost', 'Potential lost'], ['spam', 'Spam']]) },
  { key: 'status_codes', label: 'Status', options: [] },
  { key: 'exclude_statuses', label: 'Exclude statuses', options: choices([['double_request', 'Double request'], ['lead_magnet', 'Lead Magnet']]) },
  { key: 'route_ids', label: 'Route', options: [] },
  { key: 'safari_park_ids', label: 'Safari park', options: [] },
  { key: 'join_private_codes', label: 'Join / private', options: choices([['join', 'Join'], ['private', 'Private']]) },
  { key: 'climbing_tour_length_buckets', label: 'Climbing tour length', options: choices([['1-5', '1–5 days'], ['6-8', '6–8 days'], ['9+', '9+ days']]) },
  { key: 'safari_tour_length_buckets', label: 'Safari tour length', options: choices([['1-6', '1–6 days'], ['7-9', '7–9 days'], ['10+', '10+ days']]) },
]

export default {
  name: 'CRMAnalyticsCohorts',
  components: { highcharts: Chart },
  data() {
    const filterFields = fields()
    return {
      filterFields,
      filters: Object.fromEntries(filterFields.map(field => [field.key, field.key === 'exclude_statuses' ? ['double_request', 'lead_magnet'] : []])),
      period: [moment.utc().subtract(5, 'months').startOf('month').format('YYYY-MM-DD'), moment.utc().startOf('month').format('YYYY-MM-DD')],
      granularity: 'month',
      granularityOptions: [{ value: 'month', label: 'Month' }, { value: 'week', label: 'Week' }],
      quickPayment: 'include',
      paymentOptions: [{ value: 'include', label: 'Include' }, { value: 'exclude', label: 'Exclude' }, { value: 'include_only', label: 'Include only' }],
      showFilters: false,
      metrics: [
        { key: 'conversion_rate', title: 'Cohort conversion', chartTitle: 'Cumulative conversion', column: '%CR', axis: 'Conversion, %', suffix: '%' },
        { key: 'net_revenue', title: 'Net Revenue', chartTitle: 'Cumulative Net Revenue', column: 'Revenue', axis: 'Net Revenue, USD', suffix: ' USD' },
        { key: 'roas', title: 'ROAS', chartTitle: 'Cumulative ROAS', column: 'ROAS', axis: 'ROAS, ×', suffix: '×' },
      ],
      showChart: { conversion_rate: true, net_revenue: true, roas: true },
      result: null,
      selected: [],
      loading: false,
      error: '',
      optionsError: '',
      requestId: 0,
    }
  },
  computed: {
    activeFilterCount() {
      return Object.values(this.filters).filter(value => value.length).length
    },
    totalLeads() {
      return this.result ? this.result.cohorts.reduce((total, row) => total + row.lead_count, 0) : 0
    },
  },
  created() {
    this.loadOptions()
    this.loadCohorts()
  },
  beforeDestroy() {
    this.requestId += 1
  },
  methods: {
    tableColumns(metric) {
      return ['cohort', 'lead_count', ...(metric.key === 'roas' ? ['marketing_expenses'] : []), ...this.result.checkpoint_days.map(days => `checkpoint_${days}`)]
    },
    tableOptions(metric) {
      return {
        headings: {
          cohort: this.result.granularity === 'week' ? 'Week starting' : 'Cohort',
          lead_count: 'Leads',
          marketing_expenses: 'Mark. expenses',
          ...Object.fromEntries(this.result.checkpoint_days.map(days => [`checkpoint_${days}`, `${metric.column} ${days}d`]))
        },
        pagination: { show: false },
        sortable: [],
        perPage: Math.max(1, this.result.cohorts.length),
        texts: { noResults: 'No matching entries' },
        rowClassCallback: () => 'table-default__row',
        resizableColumns: false
      }
    },
    chartSeries(metricKey) {
      if (!this.result) return []
      return this.result.cohorts.filter(row => this.selected.includes(row.cohort)).map(row => ({
        id: row.cohort,
        name: this.cohortLabel(row.cohort),
        data: row.checkpoints.map(checkpoint => [checkpoint.days, checkpoint[metricKey] ?? null]),
      }))
    },
    hasChartPoints(metricKey) {
      return this.chartSeries(metricKey).some(series => series.data.some(point => point[1] !== null))
    },
    chartEmptyMessage(metricKey) {
      if (metricKey !== 'roas') return 'Select a cohort with leads and at least one completed observation window.'
      return 'Select a cohort with leads, a completed observation window and nonzero marketing expenses.'
    },
    chartOptions(metric) {
      return {
        chart: { type: 'line', height: 360 },
        title: { text: '' },
        credits: { enabled: false },
        accessibility: { enabled: false },
        xAxis: { title: { text: 'Days since lead creation' }, tickPositions: this.result ? this.result.checkpoint_days : [] },
        yAxis: metric.key === 'conversion_rate' ? { title: { text: metric.axis }, min: 0, softMax: 10, ceiling: 100, endOnTick: false } : { title: { text: metric.axis }, softMin: 0 },
        tooltip: { shared: true, headerFormat: '<b>{point.key} days</b><br/>', valueSuffix: metric.suffix, valueDecimals: 2 },
        plotOptions: { series: { connectNulls: false, marker: { enabled: true } } },
        series: this.chartSeries(metric.key),
      }
    },
    formatValue(value, metricKey) {
      if (value === null || value === undefined) return '—'
      if (metricKey === 'conversion_rate') return this.formatRate(value)
      if (metricKey === 'roas') return `${value.toFixed(2)}×`
      return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    },
    cohortLabel(value) {
      return moment.utc(value).format(this.result?.granularity === 'week' ? 'DD MMM YYYY' : 'MMM YYYY')
    },
    formatTimestamp(value) {
      return moment.utc(value).format('DD MMM YYYY HH:mm [UTC]')
    },
    formatRate(value) {
      return value === null ? '—' : `${value.toFixed(2)}%`
    },
    checkpointInfo(row, checkpoint, metricKey) {
      if (!checkpoint.is_mature) return `Full observation window available at ${this.formatTimestamp(checkpoint.available_at)}`
      if (!row.lead_count) return 'No leads in this cohort'
      if (metricKey === 'net_revenue') return `Current itinerary commission for leads that became Customer within ${checkpoint.days} days`
      if (metricKey === 'roas') return row.marketing_expenses ? `Net Revenue / ${this.formatValue(row.marketing_expenses, 'net_revenue')} marketing expenses` : 'ROAS is undefined when marketing expenses total zero'
      return `${checkpoint.customer_count} of ${row.lead_count} leads became Customer within ${checkpoint.days} days`
    },
    toggleCohort(cohort, checked) {
      this.selected = checked ? [...this.selected, cohort] : this.selected.filter(value => value !== cohort)
    },
    async loadOptions() {
      this.optionsError = ''
      const requests = [
        ['country_ids', () => getCountries(), 'code', 'name'],
        ['manager_ids', () => getUsers({ group: 'sales_manager', is_active: true }), 'id', 'full_name'],
        ['status_codes', () => getLeadsStatuses(), 'code', 'name'],
        ['adv_source_ids', () => getLeadsAdvSources(), 'code', 'name'],
        ['route_ids', () => getClimbingRoutes(), 'id', 'name'],
        ['safari_park_ids', () => getSafariNationalPark(), 'code', 'name'],
        ['domain_ids', () => getLeadsAnalyticsDashboardBuilderOptions(), 'id', 'domain_name'],
      ]
      await Promise.all(requests.map(async ([key, fetchOptions, valueKey, labelKey]) => {
        try {
          const response = await fetchOptions()
          const items = key === 'domain_ids' ? response.data.domains : response.data
          const options = (items || []).map(item => ({ value: item[valueKey], label: key === 'route_ids' ? `${item.name} ${item.duration}` : item[labelKey] }))
          this.filterFields.find(field => field.key === key).options = options
          if (key === 'status_codes') this.filterFields.find(field => field.key === 'exclude_statuses').options = options
        } catch (error) {
          this.optionsError = 'Some filter options could not be loaded.'
        }
      }))
    },
    async loadCohorts() {
      const requestId = ++this.requestId
      this.error = ''
      if (!this.period || !this.period[0] || !this.period[1]) {
        this.loading = false
        this.error = 'Choose a start and end month.'
        return
      }
      this.loading = true
      try {
        const response = await getLeadsAnalyticsCohorts({
          ...this.filters,
          granularity: this.granularity,
          date_from: moment.utc(this.period[0]).startOf('month').format('YYYY-MM-DD'),
          date_to: moment.utc(this.period[1]).endOf('month').format('YYYY-MM-DD'),
          quick_payment: this.quickPayment,
        })
        if (requestId !== this.requestId) return
        const resetSelection = !this.result || this.result.granularity !== response.data.granularity
        this.result = response.data
        this.selected = resetSelection ? this.result.cohorts.filter(row => row.lead_count && row.checkpoints.some(point => point.is_mature)).slice(-3).map(row => row.cohort) : this.selected.filter(cohort => this.result.cohorts.some(row => row.cohort === cohort))
      } catch (error) {
        if (requestId !== this.requestId) return
        this.error = error.response?.data?.details || 'Could not load cohorts. Please try again.'
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
  },
}
</script>

<style lang="sass" scoped>
.cohorts
  min-width: 0
  &__results
    display: grid
    grid-template-columns: repeat(2, minmax(0, 1fr))
    align-items: start
    gap: 24px
    & + &
      margin-top: 32px
      padding-top: 24px
      border-top: 1px solid $color-border
    @media (max-width: 1279px)
      grid-template-columns: minmax(0, 1fr)
  &__column
    min-width: 0
  &__partial
    display: block
    margin-top: 4px
    font-size: 11px
  &__section-heading
    display: flex
    align-items: center
    justify-content: space-between
    flex-wrap: wrap
    gap: 12px
    min-height: 38px
    margin-bottom: 12px
    h2
      margin: 0
  &__section-heading--info
    justify-content: flex-start
    gap: 20px
  &__info-btn
    width: 22px
    height: 22px
    border-radius: 50%
    border: 1px solid #77b017
    color: #77b017
    background: #fff
    font-size: 12px
    font-weight: 700
    line-height: 1
    cursor: pointer
  &__period
    width: 260px
    max-width: 100%
  &__payment
    width: 180px
    max-width: 100%
  &__filters
    display: grid
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr))
    gap: 16px
    margin-top: 20px
    @media (max-width: 768px)
      grid-template-columns: minmax(0, 1fr)
  &__help
    margin: 16px 0
    color: $color-text-base
    font-size: 13px
    line-height: 1.5
  &__explanation
    color: $color-text-base
    font-size: 14px
    line-height: 1.6
    h3
      margin: 20px 0 8px
      font-size: 16px
      &:first-child
        margin-top: 0
    p
      margin: 0 0 12px
  &__loading
    position: relative
    min-height: 160px
    padding-top: 120px
    text-align: center
  &__error
    margin: 20px 0
    color: $color-error
  &__table
    overflow-x: auto
</style>
